import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Upload, Trash2, Image, Plus, RefreshCw, GripVertical, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryImage = ({ img, onDelete }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let url;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/treats-images/${img.file_id}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch { setSrc(null); }
    };
    load();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [img.file_id]);

  return (
    <div className="relative group aspect-square bg-[#0A0A0A] overflow-hidden border border-[rgba(212,175,55,0.1)]" data-testid={`gallery-item-${img.file_id}`}>
      {src ? (
        <img src={src} alt={img.original_filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <button
          onClick={() => onDelete(img.file_id)}
          className="p-3 bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors"
          title="Delete"
          data-testid={`delete-gallery-${img.file_id}`}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-[#050505]/80 px-3 py-2">
        <p className="text-[#A1A1AA] text-xs truncate">{img.original_filename}</p>
      </div>
    </div>
  );
};

const AdminTreatsGallery = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/treats-gallery`);
      setImages(res.data);
    } catch {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchImages(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large`); continue; }
      if (!file.type.startsWith("image/")) { toast.error(`${file.name}: images only`); continue; }

      const fd = new FormData();
      fd.append("file", file);
      try {
        const token = localStorage.getItem("adminToken");
        await axios.post(`${API}/admin/treats-gallery`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        uploaded++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} image${uploaded > 1 ? "s" : ""} uploaded`);
      fetchImages();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (fileId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/treats-gallery/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImages(prev => prev.filter(i => i.file_id !== fileId));
      setDeleteId(null);
      toast.success("Image deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-treats-gallery-page">
      <div className="border-b border-[rgba(212,175,55,0.15)] bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-['Playfair_Display'] text-xl text-[#F5F5F0]">Treats Gallery</h1>
                <p className="text-[#52525B] text-sm">{images.length} image{images.length !== 1 ? "s" : ""} in gallery</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchImages}
                className="flex items-center gap-2 px-3 py-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.4)]"
                data-testid="refresh-gallery"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] text-sm font-semibold hover:bg-[#F7E7CE] transition-colors disabled:opacity-50"
                data-testid="upload-gallery-btn"
              >
                {uploading ? (
                  <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Upload Images
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleUpload}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 p-4 border border-[rgba(212,175,55,0.2)] bg-[#D4AF37]/5">
          <p className="text-[#A1A1AA] text-sm">
            Upload photos of your work to display in the Gallery section on the{" "}
            <a href="/treats" target="_blank" className="text-[#D4AF37] underline">Treats page</a>.
            You can upload multiple images at once. Images will replace the default stock photos.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-16 h-16 text-[#52525B]/30 mx-auto mb-4" />
            <p className="text-[#A1A1AA] text-lg mb-2">No gallery images yet</p>
            <p className="text-[#52525B] text-sm mb-6">Upload your treat photos to showcase on the Treats page</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#F7E7CE] transition-colors"
              data-testid="empty-upload-btn"
            >
              <Upload className="w-4 h-4" /> Upload Your First Images
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img) => (
              <GalleryImage
                key={img.file_id}
                img={img}
                onDelete={(id) => setDeleteId(id)}
              />
            ))}
            {/* Add more button */}
            <div
              className="aspect-square border-2 border-dashed border-[rgba(212,175,55,0.2)] flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37] transition-colors bg-[#0A0A0A]"
              onClick={() => fileInputRef.current?.click()}
              data-testid="add-more-gallery"
            >
              <Plus className="w-8 h-8 text-[#D4AF37]/40 mb-2" />
              <p className="text-[#52525B] text-sm">Add More</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteId && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeleteId(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#121212] border border-[rgba(212,175,55,0.3)] p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="delete-gallery-modal"
            >
              <h3 className="font-['Playfair_Display'] text-lg text-[#F5F5F0] mb-3">Delete Image?</h3>
              <p className="text-[#A1A1AA] text-sm mb-6">This will remove the image from the Treats gallery.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] text-sm hover:text-[#F5F5F0]">
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteId)}
                  className="flex-1 py-2 bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30"
                  data-testid="confirm-delete-gallery"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTreatsGallery;
